import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Space, Button, Col, Row, Modal, Drawer } from "antd";
import { BiFilterAlt, BiTransfer } from "react-icons/bi";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import ContentHeaderComponent from "../../shared/components/content-header/content-header.component";
import { useForm } from "antd/lib/form/Form";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import moment from "moment";
import { getVendorList } from "../../core/store/reducers/vendor.slice";
import CreateEditVendorComponent from "./components/create-edit-vendor.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";

const ManageVendorPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingVendor, vendorList, pagination } = useSelector(
    (state: RootState) => state.vendor
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [vendorIdToDelete, setVendorIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { user } = useSelector((state: RootState) => state.auth);
  // List local state
  const [vendorListData, setVendorListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState("");

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  useEffect(() => {
    loadList();
  }, [filters, sorter]);
  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getVendorList({ params: data, body: filter }));
  };
  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };
  useEffect(() => {
    setVendorListData(
      vendorList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [vendorList]);
  const onFilterApply = (values: any) => {
    setFilters(values); //filters
  };
  const onPageChange = (pageNumber: number) => {
    // loadList((pageNumber - 1) * pagination?.limit);
    loadList(pageNumber);
  };
  const hasAnyVendorAccess =
  accessAllowed(myPermissions, allPermissions.UPDATE_VENDOR) ||
  accessAllowed(myPermissions, allPermissions.VENDOR_ACTIVITY_LOGS);
  const columns = [
    {
      title: "Vendor Name",
      dataIndex: "vendorName",
      sorter: false,
      key: "vendorName",
    },
    {
      title: "Company Email",
      dataIndex: "companyEmail",
      sorter: false,
      key: "companyEmail",
    },
    {
      title: "Company Phone",
      dataIndex: "companyPhone",
      sorter: false,
      key: "companyPhone",
    },
    {
      title: "Company Address",
      dataIndex: "companyAddress",
      sorter: false,
      key: "companyAddress",
    },
    {
      title: "Service Type",
      dataIndex: "serviceTypeName",
      sorter: false,
      key: "serviceType",
    },
    ...(hasAnyVendorAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(myPermissions, allPermissions.UPDATE_VENDOR) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.VENDOR_ACTIVITY_LOGS) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
                {/* Uncomment when delete is enabled */}
                {/* {accessAllowed(myPermissions, allPermissions.DELETE_VENDOR) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.vendorId)}
                    icon={<DeleteOutlined style={{color:"#FF0000"}}  />}
                  />
                )} */}
              </Space>
            ),
          },
        ]
      : []),
  ];
  const handleDelete = (vendorId: string) => {
    // dispatch(deleteVendor(vendorId)).then((val: any) => {
    //     if (val?.payload?.data) {
    //         showNotification("Vendor Deleted", "success");
    //         loadList();
    //         setDeleteModalVisible(false); // Close the modal after successful deletion
    //     }
    // });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedVendorId(record.vendorId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setSelectedVendorId("");
  };

  const handleDeleteModal = (vendorId: string) => {
    setDeleteModalVisible(true);
    setVendorIdToDelete(vendorId);
  };

  const onDeleteConfirm = () => {
    handleDelete(vendorIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setVendorIdToDelete("");
  };

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };
  useEffect(() => {
    // dispatch(getList()).then((val: any) => {
    //   if (val?.payload?.data?.data?.length > 0) {
    //     setList(
    //       val.payload.data.data.map((val: any) => {
    //         return { label: val.name, value: val._id };
    //       })
    //     );
    //   }
    // });
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => {};

  const formProps = {
    form: form,
    name: "manage-vendor-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "name",
        label: "",
        placeholder: "Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space>
      {accessAllowed(myPermissions, allPermissions.CREATE_VENDOR) && (
        <Button
          type="primary"
          // size="large"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Vendor"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={
          <span role="img" className="anticon">
            <BiFilterAlt />
          </span>
        }
        onClick={() => {
          setFilterVisible(!filterVisible);
        }}
      ></Button>
    </Space>
  );

  return (
    <>
      <div
        className="mx-6 mt-2 bg-primary-white-blueish border-primary-gray-02"
        style={{ borderBottom: "none!important" }}
      >
        <Row>
          <Col span={24}>
            <ContentHeaderComponent
              title="Manage Vendor"
              subtitle=""
              buttons={headerButtons}
            />
          </Col>
        </Row>
        {filterVisible && (
          <Row className="bg-filter">
            <Col span={24} className="py-1">
              <div className="px-3 table-filter-form">
                <GenericFormComponent {...formProps} />
              </div>
            </Col>
          </Row>
        )}
      </div>
      <DataTableComponent
        pagination={true}
        loading={loadingVendor}
        dataSource={vendorListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
        // maxWidth="2200px"
      />
      {/* <Modal
                title="Confirm Delete"
                visible={deleteModalVisible}
                onOk={onDeleteConfirm}
                onCancel={onDeleteCancel}
            >
                <p>Are you sure you want to delete this vendor ?</p>
            </Modal> */}
      {createEditOpen && (
        <CreateEditVendorComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedVendorId}
            record={selectedEntity}
            module="Vendor"
          />
        </Drawer>
      )}
    </>
  );
};
export default ManageVendorPage;
